/**
 * Data Helper Utilities for Merchant Applications
 * Provides consistent data access patterns and normalization functions
 */

import { MerchantApplication, MerchantApplicationData } from '../types';
import { DATA_CONFIG } from '../config/constants';

/**
 * Safely extracts business name from various possible locations in the data structure
 */
export function getBusinessName(data: MerchantApplicationData | any): string {
  if (!data) return 'Unnamed Business';
  
  // Try nested structure first (preferred)
  if (data.corporationInfo?.companyName) {
    return data.corporationInfo.companyName;
  }
  
  if (data.corporationInfo?.businessName) {
    return data.corporationInfo.businessName;
  }
  
  // Try flattened structure (legacy)
  if (data.companyName) {
    return data.companyName;
  }
  
  if (data.businessName) {
    return data.businessName;
  }
  
  return 'Unnamed Business';
}

/**
 * Safely extracts DBA name from various possible locations
 */
export function getDbaName(data: MerchantApplicationData | any): string | undefined {
  if (!data) return undefined;
  
  return (
    data.corporationInfo?.dbaName ||
    data.corporationInfo?.dba ||
    data.dbaName ||
    data.dba ||
    undefined
  );
}

/**
 * Gets display name prioritizing company name, falling back to DBA, then business name
 */
export function getDisplayName(data: MerchantApplicationData | any): string {
  const businessName = getBusinessName(data);
  const dbaName = getDbaName(data);
  
  // Prioritize company name, fall back to DBA if different from business name
  if (dbaName && dbaName !== businessName) {
    return `${businessName} (DBA: ${dbaName})`;
  }
  
  return businessName;
}

/**
 * Gets customer first name from various possible locations
 * Uses first beneficial owner as contact person if available
 */
export function getCustomerFirstName(data: MerchantApplicationData | any): string | undefined {
  if (!data) return undefined;
  
  // Use first beneficial owner as contact person
  const firstOwner = data.beneficialOwners?.[0];
  if (firstOwner?.firstName) {
    return firstOwner.firstName;
  }
  
  // Fallback to other possible locations
  return (
    data.personalInformation?.firstName ||
    data.corporationInfo?.contactFirstName ||
    data.contactFirstName ||
    data.firstName ||
    undefined
  );
}

/**
 * Gets customer last name from various possible locations
 * Uses first beneficial owner as contact person if available
 */
export function getCustomerLastName(data: MerchantApplicationData | any): string | undefined {
  if (!data) return undefined;
  
  // Use first beneficial owner as contact person
  const firstOwner = data.beneficialOwners?.[0];
  if (firstOwner?.lastName) {
    return firstOwner.lastName;
  }
  
  // Fallback to other possible locations
  return (
    data.personalInformation?.lastName ||
    data.corporationInfo?.contactLastName ||
    data.contactLastName ||
    data.lastName ||
    undefined
  );
}

/**
 * Gets full customer name (first + last)
 */
export function getCustomerFullName(data: MerchantApplicationData | any): string | undefined {
  const firstName = getCustomerFirstName(data);
  const lastName = getCustomerLastName(data);
  
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  
  if (firstName) return firstName;
  if (lastName) return lastName;
  
  return undefined;
}

/**
 * Gets title for pipeline cards with priority: DBA → company name → first/last name
 */
export function getPipelineCardTitle(data: MerchantApplicationData | any): string {
  const dbaName = getDbaName(data);
  const businessName = getBusinessName(data);
  const customerName = getCustomerFullName(data);
  
  // Priority 1: DBA name if available
  if (dbaName) {
    return dbaName;
  }
  
  // Priority 2: Company/business name if available and not default
  if (businessName && businessName !== 'Unnamed Business') {
    return businessName;
  }
  
  // Priority 3: Customer first and last name
  if (customerName) {
    return customerName;
  }
  
  // Final fallback
  return 'Unknown Customer';
}

/**
 * Safely extracts beneficial owners information
 */
export function getBeneficialOwners(data: MerchantApplicationData | any): Array<{
  firstName: string;
  lastName: string;
  title?: string;
  ownershipPercentage: string;
}> {
  if (!data?.beneficialOwners || !Array.isArray(data.beneficialOwners)) {
    return [];
  }
  
  return data.beneficialOwners.filter((owner: any) => 
    owner && owner.firstName && owner.lastName
  );
}

/**
 * Gets formatted names of beneficial owners
 */
export function getOwnerNames(data: MerchantApplicationData | any): string {
  const owners = getBeneficialOwners(data);
  
  if (owners.length === 0) {
    return 'No owners listed';
  }
  
  const names = owners.map(owner => `${owner.firstName} ${owner.lastName}`);
  
  if (names.length === 1) {
    return names[0];
  }
  
  if (names.length === 2) {
    return names.join(' & ');
  }
  
  return `${names[0]} & ${names.length - 1} others`;
}

/**
 * Safely extracts processing volume with fallback
 */
export function getProcessingVolume(data: MerchantApplicationData | any): number {
  if (!data?.processingHistory) return 0;
  
  const volume = data.processingHistory.monthlyVolume;
  return typeof volume === 'number' && volume >= 0 ? volume : 0;
}

/**
 * Safely extracts phone number from various locations
 */
export function getPhoneNumber(data: MerchantApplicationData | any): string | undefined {
  if (!data) return undefined;
  
  return (
    data.corporationInfo?.businessPhone ||
    data.corporationInfo?.customerServicePhone ||
    data.businessPhone ||
    data.phone ||
    undefined
  );
}

/**
 * Safely extracts email from various locations
 */
export function getEmailAddress(data: MerchantApplicationData | any): string | undefined {
  if (!data) return undefined;
  
  return (
    data.corporationInfo?.customerServiceEmail ||
    data.corporationInfo?.customerServiceEmail ||
    data.customerServiceEmail ||
    data.email ||
    undefined
  );
}

/**
 * Normalizes application data to ensure consistent structure
 */
export function normalizeApplicationData(application: MerchantApplication): MerchantApplication {
  const data = application.data || {};
  
  // Ensure nested structure exists
  const normalizedData: MerchantApplicationData = {
    corporationInfo: {
      businessName: getBusinessName(data),
      dba: getDbaName(data),
      customerServicePhone: getPhoneNumber(data),
      customerServiceEmail: getEmailAddress(data),
      corporationType: (data as any).corporationInfo?.corporationType || (data as any).corporationType || (data as any).businessType || DATA_CONFIG.DEFAULTS.BUSINESS_TYPE,
      ...data.corporationInfo,
    },
    processingHistory: {
      monthlyVolume: getProcessingVolume(data),
      averageTicket: data.processingHistory?.averageTicket || DATA_CONFIG.DEFAULTS.AVERAGE_TICKET,
      ...data.processingHistory,
    },
    beneficialOwners: getBeneficialOwners(data),
    bankDetails: data.bankDetails,
    documents: data.documents,
    currentStep: data.currentStep,
    completedSteps: data.completedSteps || [],
  };
  
  return {
    ...application,
    data: normalizedData,
  };
}

/**
 * Checks if application data is complete for a given step
 */
export function isStepComplete(data: MerchantApplicationData | any, step: number): boolean {
  if (!data) return false;
  
  switch (step) {
    case 1: // Business Information
      return !!(
        getBusinessName(data) !== 'Unnamed Business' &&
        data.corporationInfo?.businessType &&
        data.corporationInfo?.businessDescription
      );
      
    case 2: // Processing History
      return !!(
        data.processingHistory?.monthlyVolume !== undefined &&
        data.processingHistory?.averageTicket !== undefined &&
        data.processingHistory?.isCurrentlyProcessing
      );
      
    case 3: // Beneficial Owners
      return getBeneficialOwners(data).length > 0;
      
    case 4: // Bank Details
      return !!(
        data.bankDetails?.bankName &&
        data.bankDetails?.routingNumber &&
        data.bankDetails?.accountNumber
      );
      
    case 5: // Documentation
      return !!(data.documents && Object.keys(data.documents).length > 0);
      
    default:
      return false;
  }
}

/**
 * Calculates completion percentage for an application
 */
export function getCompletionPercentage(data: MerchantApplicationData | any): number {
  const totalSteps = 5;
  let completedSteps = 0;
  
  for (let step = 1; step <= totalSteps; step++) {
    if (isStepComplete(data, step)) {
      completedSteps++;
    }
  }
  
  return Math.round((completedSteps / totalSteps) * 100);
}

/**
 * Formats monetary values consistently
 */
export function formatCurrency(amount: number, currency = DATA_CONFIG.DEFAULTS.CURRENCY): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Formats dates consistently across the application
 */
export function formatDate(date: Date | { toDate: () => Date } | string | undefined): string {
  if (!date) return 'Not set';
  
  let dateObj: Date;
  
  if (typeof date === 'string') {
    dateObj = new Date(date);
  } else if (typeof date === 'object' && 'toDate' in date) {
    dateObj = date.toDate();
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    return 'Invalid date';
  }
  
  if (isNaN(dateObj.getTime())) {
    return 'Invalid date';
  }
  
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Safely removes undefined values from objects (recursive)
 */
export function removeUndefined(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined).filter(item => item !== undefined);
  }
  
  if (obj !== null && typeof obj === 'object') {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = removeUndefined(value);
      }
      return acc;
    }, {} as any);
  }
  
  return obj;
}

/**
 * Type guard to check if an object is a valid MerchantApplication
 */
export function isMerchantApplication(obj: any): obj is MerchantApplication {
  return (
    obj &&
    typeof obj === 'object' &&
    typeof obj.id === 'string' &&
    typeof obj.status === 'string' &&
    obj.data &&
    typeof obj.data === 'object'
  );
}